import { create } from "zustand";
import type { AuthState } from "@/types";
import { Permission, UserRole } from "@/types";
import {
  clearImpersonationAccessToken,
  decodeToken,
  getImpersonationAccessToken,
  getStoredToken,
  isImpersonationPayload,
  removeStoredToken,
  setImpersonationAccessToken,
  setStoredToken,
} from "@/lib/auth";
import { postLogoutRequest } from "@/lib/postLogoutRequest";
import { queryClient } from "@/lib/queryClient";
import { setSentryUser } from "@/lib/sentry";

interface AuthStore extends AuthState {
  login: (access: string) => void;
  impersonate: (access: string) => void;
  logout: () => void;
  initAuth: () => void;
  isAdmin: () => boolean;
  isHost: () => boolean;
  isCustomer: () => boolean;
  isSalesAgent: () => boolean;
  hasPermission: (permission: Permission) => boolean;
  getCurrentUserId: () => string | undefined;
  isImpersonating: () => boolean;
}

/** Avoid N× logout when many 401s fail refresh together (parallel React Query requests). */
let logoutOnce = false;

/**
 * Read persisted JWT before the first React paint so deep links (e.g. `/handshake/:id`)
 * do not briefly see `isAuthenticated === false`, hit `ProtectedRoute` → `/login`, then
 * post-`initAuth` login route → `/` and lose the original URL.
 */
function readAuthFromStorage(): Pick<AuthState, "user" | "isAuthenticated"> {
  try {
    const impersonationToken = getImpersonationAccessToken();
    if (impersonationToken) {
      const impersonationUser = decodeToken(impersonationToken);
      if (isImpersonationPayload(impersonationUser)) {
        return { user: impersonationUser, isAuthenticated: true };
      }
      clearImpersonationAccessToken();
    }
    const token = getStoredToken();
    if (!token) {
      return { user: null, isAuthenticated: false };
    }
    const user = decodeToken(token);
    if (!user) {
      removeStoredToken();
      return { user: null, isAuthenticated: false };
    }
    return { user, isAuthenticated: true };
  } catch {
    return { user: null, isAuthenticated: false };
  }
}

const initialAuth = readAuthFromStorage();

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: initialAuth.user,
  isAuthenticated: initialAuth.isAuthenticated,

  login: (access: string) => {
    logoutOnce = false;
    const user = decodeToken(access);
    if (!user || isImpersonationPayload(user)) {
      return;
    }
    clearImpersonationAccessToken();
    setStoredToken(access);
    set({
      user,
      isAuthenticated: true,
    });
    setSentryUser(user);
  },

  impersonate: (access: string) => {
    logoutOnce = false;
    const user = decodeToken(access);
    if (!isImpersonationPayload(user)) {
      return;
    }
    setImpersonationAccessToken(access);
    queryClient.clear();
    set({
      user,
      isAuthenticated: true,
    });
    setSentryUser(user);
  },

  logout: () => {
    if (logoutOnce) {
      return;
    }
    logoutOnce = true;
    removeStoredToken();
    clearImpersonationAccessToken();
    postLogoutRequest();
    queryClient.clear();
    set({
      user: null,
      isAuthenticated: false,
    });
    setSentryUser(null);
  },

  initAuth: () => {
    logoutOnce = false;
    const next = readAuthFromStorage();
    set(next);
    setSentryUser(next.user);
  },

  isAdmin: () => {
    const { user } = get();
    return user?.role === UserRole.ADMIN;
  },

  isHost: () => {
    const { user } = get();
    return user?.role === UserRole.HOST;
  },

  isCustomer: () => {
    const { user } = get();
    return user?.role === UserRole.CUSTOMER;
  },

  isSalesAgent: () => {
    const { user } = get();
    return user?.role === UserRole.SALES_AGENT;
  },

  hasPermission: (permission: Permission) => {
    const { user } = get();
    return user?.permissions.includes(permission) ?? false;
  },

  getCurrentUserId: () => get().user?.sub,

  isImpersonating: () => isImpersonationPayload(get().user),
}));
